import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../../ui/button';
import type { CustomScreenComponentProps } from './types';
import onboardingContent from './onboardingContent.json';
import { Card, CardContent, CardTitle } from 'components/ui/card';
import { getTestId } from 'utils/testUtils';
import { Icon, IconName } from 'components/icons/Icon';
import Popup from 'components/popup';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { useResponsiveLayout } from 'hooks/useResponsiveLayout';

const pillarsData = onboardingContent.pillars;

const DESKTOP_PILLAR_SIZE = 112;
const DESKTOP_RADIUS = 215;
const MOBILE_PILLAR_SIZE = 76;
const MOBILE_RADIUS = 108;

const toPillarMetadata = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '').trim()))
    .filter((item) => item.length > 0);
};

export const Onboarding10Pillars: React.FC<CustomScreenComponentProps> = ({
  question,
  isSubmitting,
  onComplete,
  response,
}) => {
  const { isMobile } = useResponsiveLayout();
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerCenter, setContainerCenter] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const responseMetadata = useMemo(
    () => (response?.metadata as Record<string, unknown> | undefined) ?? undefined,
    [response?.metadata],
  );
  const initialCompleted = useMemo(() => {
    return toPillarMetadata(responseMetadata?.completedPillars);
  }, [responseMetadata]);
  const [completedPillars, setCompletedPillars] = useState<string[]>(initialCompleted);
  const [activePillarIndex, setActivePillarIndex] = useState<number | null>(null);
  const [isCompletionPopupOpen, setIsCompletionPopupOpen] = useState(false);
  const lastPersistSignatureRef = useRef<string>('');
  const completionFinalizedRef = useRef(false);

  const pillarSize = isMobile ? MOBILE_PILLAR_SIZE : DESKTOP_PILLAR_SIZE;
  const radius = isMobile ? MOBILE_RADIUS : DESKTOP_RADIUS;
  const canvasSize = pillarSize + radius * 2;

  const persistMetadata = useCallback(
    (completedIds: string[], shouldComplete: boolean) => {
      const normalizedIds = [...completedIds];
      const signature = `${[...normalizedIds].sort().join('|')}::${shouldComplete ? '1' : '0'}`;
      if (lastPersistSignatureRef.current === signature) {
        return;
      }

      lastPersistSignatureRef.current = signature;

      onComplete({
        metadata: { completedPillars: normalizedIds },
        is_completed: shouldComplete,
      });
    },
    [onComplete],
  );

  useEffect(() => {
    setCompletedPillars((prev) => {
      const incoming = initialCompleted;
      if (incoming.length === 0) {
        return prev;
      }
      const merged = new Set([...prev, ...incoming]);
      return Array.from(merged);
    });
  }, [initialCompleted]);

  useEffect(() => {
    if (initialCompleted.length > 0) {
      lastPersistSignatureRef.current = `${[...initialCompleted].sort().join('|')}::0`;
    }
  }, [initialCompleted]);

  useEffect(() => {
    const updateCenter = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerCenter({
          x: rect.width / 2,
          y: rect.height / 2,
        });
      }
    };

    const timer = setTimeout(updateCenter, 0);
    window.addEventListener('resize', updateCenter);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateCenter);
    };
  }, [canvasSize]);

  const ensurePillarCompleted = useCallback(
    (index: number) => {
      const pillar = pillarsData[index];
      if (!pillar || completedPillars.includes(pillar.id)) {
        return;
      }

      const updated = [...completedPillars, pillar.id];
      setCompletedPillars(updated);
      persistMetadata(updated, false);
    },
    [completedPillars, persistMetadata],
  );

  const handleOpenPillar = useCallback(
    (index: number) => {
      setActivePillarIndex(index);
      ensurePillarCompleted(index);
    },
    [ensurePillarCompleted],
  );

  useEffect(() => {
    if (activePillarIndex === null) {
      return;
    }
    ensurePillarCompleted(activePillarIndex);
  }, [activePillarIndex, ensurePillarCompleted]);

  const allCompleted = completedPillars.length >= pillarsData.length;

  const handleAdvancePillar = useCallback(() => {
    if (activePillarIndex === null) {
      return;
    }

    const current = pillarsData[activePillarIndex];
    if (!current) {
      return;
    }

    const alreadyCompleted = completedPillars.includes(current.id);
    const updatedSet = new Set(completedPillars);
    updatedSet.add(current.id);
    const updatedList = Array.from(updatedSet);

    if (!alreadyCompleted) {
      setCompletedPillars(updatedList);
    }
    persistMetadata(updatedList, false);

    const sequentialNextIndex = pillarsData.findIndex(
      (item, idx) => idx > activePillarIndex && !updatedSet.has(item.id),
    );
    const fallbackIndex =
      sequentialNextIndex !== -1
        ? sequentialNextIndex
        : pillarsData.findIndex((item) => !updatedSet.has(item.id));

    setActivePillarIndex(fallbackIndex === -1 ? null : fallbackIndex);
  }, [activePillarIndex, completedPillars, persistMetadata]);

  const handleSkipIntroductions = useCallback(() => {
    const completedIds = pillarsData.map((pillar) => pillar.id);
    setCompletedPillars(completedIds);
    persistMetadata(completedIds, false);
    setActivePillarIndex(null);
    setIsCompletionPopupOpen(true);
  }, [persistMetadata]);

  const getPillarPosition = (index: number) => {
    if (!containerCenter) return { top: 0, left: 0 };

    const angleStep = (2 * Math.PI) / pillarsData.length;
    const startAngle = -Math.PI / 2;
    const angle = startAngle + index * angleStep;

    const x = containerCenter.x - pillarSize / 2 + radius * Math.cos(angle);
    const y = containerCenter.y - pillarSize / 2 + radius * Math.sin(angle);

    return { top: Math.round(y), left: Math.round(x) };
  };

  const activePillar = activePillarIndex !== null ? pillarsData[activePillarIndex] : null;
  const completedSet = useMemo(() => new Set(completedPillars), [completedPillars]);
  const completedCount = completedPillars.length;
  const baseProgress = completedCount + 1;
  const progressPosition =
    activePillar && completedSet.has(activePillar.id)
      ? Math.min(pillarsData.length, completedCount)
      : Math.min(pillarsData.length, baseProgress);

  const handleSeePillars = useCallback(() => {
    if (isSubmitting || !allCompleted) {
      return;
    }
    completionFinalizedRef.current = false;
    setIsCompletionPopupOpen(true);
  }, [allCompleted, isSubmitting]);

  const finalizeCompletion = useCallback(() => {
    if (completionFinalizedRef.current) {
      return;
    }
    completionFinalizedRef.current = true;
    setIsCompletionPopupOpen(false);
    const completedIds = Array.from(new Set(completedPillars));
    persistMetadata(completedIds, true);
  }, [completedPillars, persistMetadata]);

  return (
    <>
      <Card
        {...getTestId(`question-card-${question.id}`)}
        padding="none"
        className="w-full max-w-[62.5rem] bg-transparent"
        maxWidth="none"
      >
        <CardTitle
          title="10 Pillars of High Performance Living"
          subtitle="Click each topic to learn more"
          centered
        />
        <CardContent variant="userResponse" padding="large" className="w-full flex-1 rounded-3xl">
          <div
            ref={containerRef}
            className="relative mx-auto py-6"
            style={{ width: canvasSize, height: canvasSize }}
          >
            {containerCenter &&
              pillarsData.map((pillar, index) => (
                <Pillar
                  key={pillar.id}
                  icon={pillar.icon as IconName}
                  label={pillar.label}
                  size={pillarSize}
                  isCompleted={completedSet.has(pillar.id)}
                  onOpen={() => handleOpenPillar(index)}
                  position={getPillarPosition(index)}
                />
              ))}
          </div>
        </CardContent>
        <div className="p-6">
          <TooltipProvider disableHoverableContent>
            <Tooltip delayDuration={400}>
              <TooltipTrigger asChild>
                <div className="inline-flex">
                  <Button
                    onClick={handleSeePillars}
                    disabled={isSubmitting || !allCompleted}
                    type="button"
                    variant="secondary"
                    size="auto"
                  >
                    See my 10 Pillars
                  </Button>
                </div>
              </TooltipTrigger>
              {!allCompleted ? (
                <TooltipContent side="top">
                  Click on each pillar before continuing
                </TooltipContent>
              ) : null}
            </Tooltip>
          </TooltipProvider>
        </div>
      </Card>

      {activePillar && (
        <Popup
          onClose={() => setActivePillarIndex(null)}
          header={`${activePillar.header} · ${progressPosition} of ${pillarsData.length}`}
          title={activePillar.label}
          description={activePillar.body}
          actions={(
            <div className="flex w-full flex-col items-stretch gap-2">
              <Button onClick={handleAdvancePillar} size="auto">
                {completedSet.size + (completedSet.has(activePillar.id) ? 0 : 1) >= pillarsData.length ? 'Done' : 'Next Pillar'}
              </Button>
              {!allCompleted ? (
                <button
                  type="button"
                  className="type-body-s px-3 py-2 text-foundation-base-darkbase-dark-400 underline underline-offset-[3px]"
                  onClick={handleSkipIntroductions}
                >
                  Skip introductions
                </button>
              ) : null}
            </div>
          )}
          isSubmitting={false}
        >
          <p className="mt-4 text-center text-xs text-muted-foreground">
            You can explore every pillar now or skip ahead to your results.
          </p>
        </Popup>
      )}
      {isCompletionPopupOpen && (
        <Popup
          onClose={finalizeCompletion}
          title="Hooray! Now that you've been briefly introduced to all 10, let's see how you rated yourself this season ..."
          description="Continue to see your personal 10 Pillars results."
          buttonText="See my 10 Pillars"
          onClickButton={finalizeCompletion}
          isSubmitting={false}
          icon="orangeCircles"
          iconSize={110}
        >
          <div className="flex flex-col items-center text-foundation-base-darkbase-dark-300 font-body-s text-[0.7rem] tracking-[var(--body-s-letter-spacing)] leading-[var(--body-s-line-height)] [font-style:var(--body-s-font-style)]">
            <div className="flex items-center gap-1">
              <Icon name="flame" size={12} />1-Day Streak
            </div>
            <span>Total 10 Pillar check-ins complete: 1</span>
          </div>
        </Popup>
      )}
    </>
  );
};

interface PillarProps {
  icon: IconName;
  label: string;
  size: number;
  position: { top: number; left: number };
  isCompleted: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onOpen: () => void;
}

const Pillar: React.FC<PillarProps> = ({
  icon,
  label,
  size,
  position,
  isCompleted,
  onClick,
  onOpen,
}) => {
  const compact = size < 90;
  const iconSize = icon === 'fork' ? (compact ? 30 : 40) : (compact ? 24 : 32);
  return (
    <button
      className="absolute group cursor-pointer rounded-full transition-transform hover:scale-105"
      style={{ top: `${position.top}px`, left: `${position.left}px`, width: `${size}px`, height: `${size}px` }}
      onClick={(event) => {
        onOpen();
        onClick?.(event);
      }}
      aria-label={label.replace(/\n/g, ' ')}
    >
      <div
        className={`flex h-full w-full flex-col items-center justify-center rounded-full transition-colors ${
          isCompleted
            ? 'bg-surface-pillars-soft/70 group-hover:bg-surface-pillars-hover/60'
            : 'bg-white/70 group-hover:bg-surface-pillars-hover/90'
        } ${compact ? 'gap-1 p-2 pt-2.5' : 'gap-2 p-3 pt-4'}`}
      >
        <Icon name={icon} size={iconSize} />
        <span
          className={`font-body-XS-semibold font-[number:var(--body-XS-semibold-font-weight)] text-center break-words text-foundation-orange-900 tracking-[var(--body-XS-semibold-letter-spacing)] [font-style:var(--body-XS-semibold-font-style)] ${compact ? 'text-[0.62rem] leading-tight' : 'text-[length:var(--body-XS-semibold-font-size)] leading-[var(--body-XS-semibold-line-height)]'}`}
        >
          {label}
        </span>
      </div>
    </button>
  );
};
